import { StateCreator } from 'zustand';
import { produce } from 'immer';
import { createAppStore } from '@marriott/mi-store-utils';

/**
 * Session object structure for SearchResults renderedData
 */
export interface SessionObject {
  consumerID: string;
  accessToken: string;
  sessionID: string;
}

/**
 * SearchResults renderedData state interface
 * Mirrors the existing SearchResults renderedData context structure
 */
export interface SearchResultsRenderedDataState {
  jsonContent: Record<string, unknown>;
  configData: Record<string, unknown>;
  isMapViewEnabled: boolean;
  recentlyViewed: unknown[];
  featureSelected: boolean;
  isFlexibleDateSearch: boolean;
  isSearchClicked: boolean;
  isStateOrCountry: boolean;
  continueCtaProperty: string;
  sessionObject: SessionObject;
  revisionToken: string;
  savedProperties: string[];
  formValues: Record<string, unknown>;
  isSpecialRateSearch: boolean;
  isAutoSuggestSelectedSearch: boolean;
  isRecentViewSelectedSearch: boolean;
  isErrorState: boolean;
  isLastCardProcessed: boolean;
  imageURL: string;
  isTaxesandFeesIncludedFromPopup: boolean;
  isStaticMap: boolean;
  isMapLoading: boolean;
}

/**
 * Actions available for the SearchResults renderedData store
 */
export interface SearchResultsRenderedDataActions {
  /**
   * Set the map view enabled state
   */
  setISMapViewEnabled: (enabled: boolean) => void;

  /**
   * Set the recently viewed items
   */
  setRecentlyViewed: (items: unknown[]) => void;

  /**
   * Set the feature selected state
   */
  setFeatureSelected: (selected: boolean) => void;

  /**
   * Set the flexible date search state
   */
  setIsFlexibleDateSearch: (enabled: boolean) => void;

  /**
   * Set the search clicked state
   */
  setIsSearchClicked: (clicked: boolean) => void;

  /**
   * Set the state or country flag
   */
  setIsStateOrCountry: (isStateOrCountry: boolean) => void;

  /**
   * Set the continue CTA property
   */
  setContinueCtaProperty: (property: string) => void;

  /**
   * Set the session object
   */
  setSessionObject: (sessionObject: SessionObject) => void;

  /**
   * Set the revision token
   */
  setRevisionToken: (token: string | object) => void;

  /**
   * Set the saved properties
   */
  setSavedProperties: (savedProperties: string[]) => void;

  /**
   * Set the form values
   */
  setFormValues: (formValues: Record<string, unknown>) => void;

  /**
   * Set the special rate search state
   */
  setIsSpecialRateSearch: (enabled: boolean) => void;

  /**
   * Set the auto suggest selected search state
   */
  setIsAutoSuggestSelectedSearch: (value: boolean) => void;

  /**
   * Set the recent view selected search state
   */
  setIsRecentViewSelectedSearch: (value: boolean) => void;

  /**
   * Set the error state
   */
  setIsErrorState: (error: boolean) => void;

  /**
   * Set the last card processed state
   */
  setIsLastCardProcessed: (processed: boolean) => void;

  /**
   * Set the image URL
   */
  setImageURL: (url: string) => void;

  /**
   * Set the taxes and fees included from popup state
   */
  setIsTaxesandFeesIncludedFromPopup: (value: boolean) => void;

  /**
   * Initialize the SearchResults renderedData store with context data
   */
  initializeSearchResultsRenderedData: (data: Partial<SearchResultsRenderedDataState>) => void;

  /**
   * Reset SearchResults renderedData store to default state
   */
  resetSearchResultsRenderedData: () => void;
}

/**
 * Combined SearchResults renderedData store interface
 */
export type SearchResultsRenderedDataStore = SearchResultsRenderedDataState & SearchResultsRenderedDataActions;

/**
 * Initial state for the SearchResults renderedData store
 * Matches the default values from the original context
 */
const initialSearchResultsRenderedDataState: SearchResultsRenderedDataState = {
  jsonContent: {},
  configData: {}, // Will be initialized with SM config when store is created
  isMapViewEnabled: false,
  recentlyViewed: [],
  featureSelected: false,
  isFlexibleDateSearch: false,
  isSearchClicked: false,
  isStateOrCountry: false,
  continueCtaProperty: '',
  sessionObject: { consumerID: '', accessToken: '', sessionID: '' },
  revisionToken: '',
  savedProperties: [],
  formValues: {},
  isSpecialRateSearch: false,
  isAutoSuggestSelectedSearch: false,
  isRecentViewSelectedSearch: false,
  isErrorState: true,
  isLastCardProcessed: false,
  imageURL: '',
  isTaxesandFeesIncludedFromPopup: false,
  isStaticMap: false,
  isMapLoading: false,
};

/**
 * SearchResults renderedData Zustand store implementation
 *
 * This store manages SearchResults renderedData state to replace the renderedData Context
 * in SearchResultsMainComponent. It maintains the same API surface for seamless migration.
 *
 * Key features:
 * - Exact same state properties as original context (20+ properties)
 * - Actions with same signatures as context setters
 * - Initialization method for seamless migration from context
 * - Reset functionality for testing/cleanup
 * - Non-persistent store (search result state should not persist across sessions)
 */
const searchResultsRenderedDataStore: StateCreator<SearchResultsRenderedDataStore> = set => ({
  ...initialSearchResultsRenderedDataState,

  /**
   * Set the map view enabled state
   */
  setISMapViewEnabled: (enabled: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isMapViewEnabled = enabled;
      })
    );
  },

  /**
   * Set the recently viewed items
   */
  setRecentlyViewed: (items: unknown[]) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.recentlyViewed = items;
      })
    );
  },

  /**
   * Set the feature selected state
   */
  setFeatureSelected: (selected: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.featureSelected = selected;
      })
    );
  },

  /**
   * Set the flexible date search state
   */
  setIsFlexibleDateSearch: (enabled: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isFlexibleDateSearch = enabled;
      })
    );
  },

  /**
   * Set the search clicked state
   */
  setIsSearchClicked: (clicked: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isSearchClicked = clicked;
      })
    );
  },

  /**
   * Set the state or country flag
   */
  setIsStateOrCountry: (isStateOrCountry: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isStateOrCountry = isStateOrCountry;
      })
    );
  },

  /**
   * Set the continue CTA property
   */
  setContinueCtaProperty: (property: string) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.continueCtaProperty = property;
      })
    );
  },

  /**
   * Set the session object
   */
  setSessionObject: (sessionObject: SessionObject) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.sessionObject = sessionObject;
      })
    );
  },

  /**
   * Set the revision token
   */
  setRevisionToken: (token: string | object) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.revisionToken = typeof token === 'string' ? token : JSON.stringify(token);
      })
    );
  },

  /**
   * Set the saved properties
   */
  setSavedProperties: (savedProperties: string[]) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.savedProperties = savedProperties;
      })
    );
  },

  /**
   * Set the form values
   */
  setFormValues: (formValues: Record<string, unknown>) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.formValues = formValues;
      })
    );
  },

  /**
   * Set the special rate search state
   */
  setIsSpecialRateSearch: (enabled: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isSpecialRateSearch = enabled;
      })
    );
  },

  /**
   * Set the auto suggest selected search state
   */
  setIsAutoSuggestSelectedSearch: (value: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isAutoSuggestSelectedSearch = value;
      })
    );
  },

  /**
   * Set the recent view selected search state
   */
  setIsRecentViewSelectedSearch: (value: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isRecentViewSelectedSearch = value;
      })
    );
  },

  /**
   * Set the error state
   */
  setIsErrorState: (error: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isErrorState = error;
      })
    );
  },

  /**
   * Set the last card processed state
   */
  setIsLastCardProcessed: (processed: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isLastCardProcessed = processed;
      })
    );
  },

  /**
   * Set the image URL
   */
  setImageURL: (url: string) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.imageURL = url;
      })
    );
  },

  /**
   * Set the taxes and fees included from popup state
   */
  setIsTaxesandFeesIncludedFromPopup: (value: boolean) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        state.isTaxesandFeesIncludedFromPopup = value;
      })
    );
  },

  /**
   * Initialize the SearchResults renderedData store with context data
   * This is called from the conditional hook when the store should be used
   */
  initializeSearchResultsRenderedData: (data: Partial<SearchResultsRenderedDataState>) => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        Object.keys(data).forEach(key => {
          const typedKey = key as keyof SearchResultsRenderedDataState;
          if (data[typedKey] !== undefined) {
            // Type-safe assignment using object spread
            Object.assign(state, { [typedKey]: data[typedKey] });
          }
        });
      })
    );
  },

  /**
   * Reset SearchResults renderedData store to default state
   * Useful for testing and cleanup scenarios
   */
  resetSearchResultsRenderedData: () => {
    set(
      produce((state: SearchResultsRenderedDataStore) => {
        Object.assign(state, initialSearchResultsRenderedDataState);
      })
    );
  },
});

/**
 * Zustand store hook for SearchResults renderedData
 *
 * Usage:
 * ```typescript
 * const searchResultsStore = useSearchResultsRenderedDataStore();
 * const isMapViewEnabled = searchResultsStore.isMapViewEnabled;
 * searchResultsStore.setISMapViewEnabled(true);
 * ```
 *
 * Note: This store is non-persistent as search result state should not persist across browser sessions
 */
export const useSearchResultsRenderedDataStore = createAppStore(searchResultsRenderedDataStore, {
  usePersistentStore: false,
});

/**
 * Store selector helpers for common use cases
 */
export const searchResultsRenderedDataStoreSelectors = {
  // State selectors
  jsonContent: (state: SearchResultsRenderedDataStore) => state.jsonContent,
  configData: (state: SearchResultsRenderedDataStore) => state.configData,
  isMapViewEnabled: (state: SearchResultsRenderedDataStore) => state.isMapViewEnabled,
  recentlyViewed: (state: SearchResultsRenderedDataStore) => state.recentlyViewed,
  featureSelected: (state: SearchResultsRenderedDataStore) => state.featureSelected,
  isFlexibleDateSearch: (state: SearchResultsRenderedDataStore) => state.isFlexibleDateSearch,
  isSearchClicked: (state: SearchResultsRenderedDataStore) => state.isSearchClicked,
  isStateOrCountry: (state: SearchResultsRenderedDataStore) => state.isStateOrCountry,
  continueCtaProperty: (state: SearchResultsRenderedDataStore) => state.continueCtaProperty,
  sessionObject: (state: SearchResultsRenderedDataStore) => state.sessionObject,
  revisionToken: (state: SearchResultsRenderedDataStore) => state.revisionToken,
  savedProperties: (state: SearchResultsRenderedDataStore) => state.savedProperties,
  formValues: (state: SearchResultsRenderedDataStore) => state.formValues,
  isSpecialRateSearch: (state: SearchResultsRenderedDataStore) => state.isSpecialRateSearch,
  isAutoSuggestSelectedSearch: (state: SearchResultsRenderedDataStore) => state.isAutoSuggestSelectedSearch,
  isRecentViewSelectedSearch: (state: SearchResultsRenderedDataStore) => state.isRecentViewSelectedSearch,
  isErrorState: (state: SearchResultsRenderedDataStore) => state.isErrorState,
  isLastCardProcessed: (state: SearchResultsRenderedDataStore) => state.isLastCardProcessed,
  imageURL: (state: SearchResultsRenderedDataStore) => state.imageURL,
  isTaxesandFeesIncludedFromPopup: (state: SearchResultsRenderedDataStore) => state.isTaxesandFeesIncludedFromPopup,
  isStaticMap: (state: SearchResultsRenderedDataStore) => state.isStaticMap,
  isMapLoading: (state: SearchResultsRenderedDataStore) => state.isMapLoading,

  // Action selectors
  setISMapViewEnabled: (state: SearchResultsRenderedDataStore) => state.setISMapViewEnabled,
  setRecentlyViewed: (state: SearchResultsRenderedDataStore) => state.setRecentlyViewed,
  setFeatureSelected: (state: SearchResultsRenderedDataStore) => state.setFeatureSelected,
  setIsFlexibleDateSearch: (state: SearchResultsRenderedDataStore) => state.setIsFlexibleDateSearch,
  setIsSearchClicked: (state: SearchResultsRenderedDataStore) => state.setIsSearchClicked,
  setIsStateOrCountry: (state: SearchResultsRenderedDataStore) => state.setIsStateOrCountry,
  setContinueCtaProperty: (state: SearchResultsRenderedDataStore) => state.setContinueCtaProperty,
  setSessionObject: (state: SearchResultsRenderedDataStore) => state.setSessionObject,
  setRevisionToken: (state: SearchResultsRenderedDataStore) => state.setRevisionToken,
  setSavedProperties: (state: SearchResultsRenderedDataStore) => state.setSavedProperties,
  setFormValues: (state: SearchResultsRenderedDataStore) => state.setFormValues,
  setIsSpecialRateSearch: (state: SearchResultsRenderedDataStore) => state.setIsSpecialRateSearch,
  setIsAutoSuggestSelectedSearch: (state: SearchResultsRenderedDataStore) => state.setIsAutoSuggestSelectedSearch,
  setIsRecentViewSelectedSearch: (state: SearchResultsRenderedDataStore) => state.setIsRecentViewSelectedSearch,
  setIsErrorState: (state: SearchResultsRenderedDataStore) => state.setIsErrorState,
  setIsLastCardProcessed: (state: SearchResultsRenderedDataStore) => state.setIsLastCardProcessed,
  setImageURL: (state: SearchResultsRenderedDataStore) => state.setImageURL,
  setIsTaxesandFeesIncludedFromPopup: (state: SearchResultsRenderedDataStore) =>
    state.setIsTaxesandFeesIncludedFromPopup,

  // Utility selectors
  initializeSearchResultsRenderedData: (state: SearchResultsRenderedDataStore) =>
    state.initializeSearchResultsRenderedData,
  resetSearchResultsRenderedData: (state: SearchResultsRenderedDataStore) => state.resetSearchResultsRenderedData,
};
